// src/pages/AddBank.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Autocomplete,
  Snackbar,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { ArrowBack, Person, Badge } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { getData, postData } from "../services/request";

const AddBank = () => {
  const navigate = useNavigate();

  // States
  const [countries, setCountries] = useState([]);
  const [banks, setBanks] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedBank, setSelectedBank] = useState(null);
  const [accountNumber, setAccountNumber] = useState("");
  const [accountName, setAccountName] = useState("");
  const [loading, setLoading] = useState(false);
  const [banksLoading, setBanksLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // 1. Load Countries on mount
  useEffect(() => {
    const loadCountries = async () => {
      try {
        const response = await getData("/api/bank/countries");
        if (Array.isArray(response)) {
          setCountries(response);
        } else if (response?.data && Array.isArray(response.data)) {
          setCountries(response.data);
        } else {
          setSnackbar({
            open: true,
            message: "Failed to load countries",
            severity: "error",
          });
        }
      } catch (error) {
        console.error(error);
        setSnackbar({
          open: true,
          message: "Network error. Please try again.",
          severity: "error",
        });
      }
    };
    loadCountries();
  }, []);

  // 2. Load Banks when Country is selected
  const handleCountryChange = async (event, newValue) => {
    setSelectedCountry(newValue);
    setSelectedBank(null); // Reset bank when country changes
    setBanks([]); // Clear banks

    if (!newValue) return;

    setBanksLoading(true);
    try {
      const response = await postData("/api/bank/bankList", {
        countryId: newValue.id,
      });

      if (Array.isArray(response)) {
        setBanks(response);
      } else if (response?.data && Array.isArray(response.data)) {
        setBanks(response.data);
      } else {
        setBanks([]);
        setSnackbar({
          open: true,
          message: "No banks found for this country",
          severity: "info",
        });
      }
    } catch (error) {
      console.error(error);
      setSnackbar({
        open: true,
        message: "Failed to load banks. Please try again.",
        severity: "error",
      });
    } finally {
      setBanksLoading(false);
    }
  };

  // 3. Add Bank API Call (Updated Payload)
  const handleAddBank = async () => {
    if (!selectedCountry || !selectedBank || !accountNumber || !accountName) {
      setSnackbar({
        open: true,
        message: "Please fill all fields",
        severity: "warning",
      });
      return;
    }

    if (accountNumber.length < 5 || accountNumber.length > 20) {
      setSnackbar({
        open: true,
        message: "Please enter a valid account number",
        severity: "warning",
      });
      return;
    }

    setLoading(true);
    try {
      // ✅ Backend ke exact payload ke mutabiq
      const payload = {
        account_number: accountNumber,
        account_name: accountName,
        bank_name: selectedBank.name, // Bank ka naam
        bank_code: String(selectedBank.code), // Bank ka code
      };

      // ✅ Naya Endpoint
      const response = await postData("/api/user/add_bank_account", payload);

      // ✅ Backend response check
      if (response?.status === true || response?.success === true) {
        setSnackbar({
          open: true,
          message: "Bank account added successfully!",
          severity: "success",
        });
        setTimeout(() => navigate("/"), 1500);
      } else {
        setSnackbar({
          open: true,
          message: response?.message || "Failed to add bank account",
          severity: "error",
        });
      }
    } catch (error) {
      console.error(error);
      setSnackbar({
        open: true,
        message: "Network error. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        background: "linear-gradient(135deg, #ffffff 0%, #f5f0ff 100%)",
        py: 2,
      }}
    >
      <Box
        sx={{
          width: { xs: "90%", sm: 500 },
          backgroundColor: "#fff",
          borderRadius: "24px",
          padding: { xs: "20px", sm: "30px" },
          boxShadow: "0px 15px 50px rgba(82, 5, 123, 0.1)",
        }}
      >
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <Typography
            variant="h5"
            sx={{
              color: "#52057b",
              fontWeight: "bold",
            }}
          >
            Add Bank
          </Typography>
        </Box>

        <Typography
          variant="h6"
          sx={{
            color: "#52057b",
            fontWeight: "600",
            mb: 3,
            fontSize: "1.1rem",
          }}
        >
          Add bank account information
        </Typography>

        {/* Form */}
        <Box component="form">
          {/* Select Country */}
          <Typography
            sx={{
              color: "#52057b",
              fontWeight: 600,
              mb: 1,
              fontSize: "0.9rem",
            }}
          >
            Select Country
          </Typography>
          <Autocomplete
            options={countries}
            getOptionLabel={(option) => option.name}
            value={selectedCountry}
            onChange={handleCountryChange}
            loading={countries.length === 0}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select Country"
                variant="outlined"
                sx={{
                  mb: 3,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    boxShadow: "0px 2px 8px rgba(0,0,0,0.05)",
                    "&.Mui-focused": {
                      boxShadow: "0px 4px 15px rgba(82, 5, 123, 0.2)",
                    },
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e0e0e0",
                  },
                }}
              />
            )}
          />

          {/* Select Bank (Depends on Country) */}
          <Typography
            sx={{
              color: "#52057b",
              fontWeight: 600,
              mb: 1,
              fontSize: "0.9rem",
            }}
          >
            Select Bank
          </Typography>
          <Autocomplete
            options={banks}
            getOptionLabel={(option) => option.name}
            value={selectedBank}
            onChange={(e, newValue) => setSelectedBank(newValue)}
            loading={banksLoading}
            disabled={!selectedCountry}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={
                  selectedCountry ? "Select Bank" : "Select country first"
                }
                variant="outlined"
                sx={{
                  mb: 3,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    boxShadow: "0px 2px 8px rgba(0,0,0,0.05)",
                    "&.Mui-focused": {
                      boxShadow: "0px 4px 15px rgba(82, 5, 123, 0.2)",
                    },
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e0e0e0",
                  },
                }}
              />
            )}
          />

          {/* Account Number */}
          <Typography
            sx={{
              color: "#52057b",
              fontWeight: 600,
              mb: 1,
              fontSize: "0.9rem",
            }}
          >
            Account Number
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Enter account number"
            value={accountNumber}
            onChange={(e) =>
              setAccountNumber(e.target.value.replace(/[^0-9]/g, ""))
            }
            sx={{
              mb: 3,
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                boxShadow: "0px 2px 8px rgba(0,0,0,0.05)",
                "&.Mui-focused": {
                  boxShadow: "0px 4px 15px rgba(82, 5, 123, 0.2)",
                },
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#e0e0e0",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Badge sx={{ color: "#52057b" }} />
                </InputAdornment>
              ),
            }}
          />

          {/* Account Name */}
          <Typography
            sx={{
              color: "#52057b",
              fontWeight: 600,
              mb: 1,
              fontSize: "0.9rem",
            }}
          >
            Account Name
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Enter account name"
            value={accountName}
            onChange={(e) => setAccountName(e.target.value)}
            sx={{
              mb: 5,
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                boxShadow: "0px 2px 8px rgba(0,0,0,0.05)",
                "&.Mui-focused": {
                  boxShadow: "0px 4px 15px rgba(82, 5, 123, 0.2)",
                },
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#e0e0e0",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Person sx={{ color: "#52057b" }} />
                </InputAdornment>
              ),
            }}
          />

          {/* Add Bank Button */}
          <Button
            fullWidth
            variant="contained"
            onClick={handleAddBank}
            disabled={loading}
            sx={{
              bgcolor: "#52057b",
              color: "#fff",
              py: 2,
              borderRadius: "12px",
              fontWeight: "bold",
              fontSize: "1.1rem",
              textTransform: "none",
              boxShadow: "0px 10px 25px rgba(82, 5, 123, 0.3)",
              "&:hover": {
                bgcolor: "#3e055e",
                boxShadow: "0px 15px 35px rgba(82, 5, 123, 0.4)",
                transform: "translateY(-2px)",
              },
              "&.Mui-disabled": {
                bgcolor: "#e0e0e0",
                color: "#9e9e9e",
                boxShadow: "none",
              },
            }}
          >
            {loading ? (
              <CircularProgress size={24} sx={{ color: "#fff" }} />
            ) : (
              "Add Bank"
            )}
          </Button>
        </Box>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            severity={snackbar.severity}
            sx={{ width: "100%", borderRadius: "12px" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default AddBank;
